// ---------------------------------------------------------------------------
// mock ของ api-content.js — handler ใช้ชื่อ action เดียวกับ GS (docs/API.md) และคืนรูปแบบเดียวกับ GS
// เก็บใน localStorage ชุดเดียวกับ mock.js (aitalk:mock:*) ; ไม่ยิงเน็ต
// ---------------------------------------------------------------------------
import { MOCK_LATENCY } from "./config";
import { getStoredProfile } from "./apiClient";
import { readStore, writeStore, mockUsers } from "./mock";
import { SAMPLE_CHARACTERS, SAMPLE_UNIVERSES, SAMPLE_CREATORS, SAMPLE_PACKAGES, AI_MODELS, SAMPLE_GIFTS, SAMPLE_POSTS, CLUB_TIERS } from "./mockData";

const uid = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const fail = (msg, code) => {
  const e = new Error(msg);
  if (code) e.code = code;
  throw e;
};
const nowIso = () => new Date().toISOString();
const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString();

const T = (id, category, sort) => ({ id, name: id, category, sort, isActive: true });
export const MOCK_TAGS = [
  T("PG", "rating", 1),
  T("ธงขาว", "flag", 2), T("ธงเขียว", "flag", 3), T("ธงเหลือง", "flag", 4), T("ธงแดง", "flag", 5),
  T("ชาย", "type", 6), T("หญิง", "type", 7), T("ตลก", "type", 8), T("โรแมนติก", "type", 9), T("แฟนตาซี", "type", 10), T("ผจญภัย", "type", 11),
  T("นักศึกษา", "role", 12), T("นักธุรกิจ", "role", 13), T("ตัวร้าย", "role", 14), T("มาเฟีย", "role", 15), T("ขุนนาง", "role", 16),
  T("นักกีฬา", "role", 17), T("วิศวะ", "role", 18), T("ไอดอล", "role", 19), T("ฮีโร่", "role", 20), T("ศิลปิน", "role", 21),
  T("ขี้อาย", "personality", 22), T("ร่าเริง", "personality", 23), T("ใจเย็น", "personality", 24), T("ปากแข็ง", "personality", 25),
  T("ออริจินอล", "media", 26), T("อนิเมะ", "media", 27), T("เกม", "media", 28),
  T("ไทย", "lang", 29), T("อังกฤษ", "lang", 30),
  T("ฮาโลวีน", "event", 31),
];

const me = () => {
  const u = mockUsers.get(getStoredProfile()?.email);
  if (!u) fail("กรุณาเข้าสู่ระบบ", "unauthorized");
  return u;
};
const requireSuper = () => {
  const u = me();
  if (u.user_type !== "super_user") fail("ไม่มีสิทธิ์ (เฉพาะ super_user)", "forbidden");
  return u;
};
const creatorOf = (id) => SAMPLE_CREATORS.find((c) => c.id === id);
const creatorName = (id) => creatorOf(id)?.displayName || Object.values(mockUsers.all()).find((u) => u.id === id)?.displayName || "ครีเอเตอร์";
const flagOfTags = (tags = []) => (tags.includes("ธงแดง") ? "red" : tags.includes("ธงเหลือง") ? "yellow" : tags.includes("ธงเขียว") ? "green" : "white");

// ---- ตัวละคร -----------------------------------------------------------------
const mineRows = () => readStore("myCharacters", []);
const draftRows = () => readStore("drafts", []);
const reviews = () => readStore("reviews", {});

/** โค้ดแชร์ 7 ตัว (A-Z2-9) คงที่จาก id — จำลองของ GS */
export const mockShareCode = (id) => {
  const ch = "ABCDEFGHIJKLMNOPQRSTUVWXYZ23456789";
  let h = 2166136261;
  let s = "";
  for (let i = 0; i < 7; i++) {
    for (const k of `${id}:${i}`) h = Math.imul(h ^ k.charCodeAt(0), 16777619) >>> 0;
    s += ch[h % ch.length];
  }
  return s;
};
const toGs = (c, status = "approved") => {
  const rv = reviews()[c.id] || {};
  return {
    shareCode: mockShareCode(c.id), contentRating: "PG", flag: flagOfTags(c.tags), baseLanguage: "th", visibility: "public", commentCount: 0,
    giftCount: Math.round((c.chatCount || 0) / 20), isArchived: false, status, ...c,
    tagIds: c.tagIds || c.tags || [], createdBy: c.creatorId, creatorName: creatorName(c.creatorId),
    isFeatured: rv.isFeatured ?? ["c1", "c3"].includes(c.id),
    ...(rv.status ? { status: rv.status } : {}),
    createdAt: c.createdAt || daysAgo(30 - (Number(String(c.id).replace(/\D/g, "")) || 0)),
  };
};
const publicChars = () => [...mineRows().map((c) => toGs(c)), ...SAMPLE_CHARACTERS.map((c) => toGs(c))];
const visibleChars = () =>
  publicChars().filter((c) => c.status !== "hidden" && c.status !== "rejected" && !c.isArchived && (c.visibility === "public" || c.createdBy === me().id));

const paginate = (rows, { page = 1, limit = 24 } = {}) => {
  const p = Number(page) || 1;
  const l = Math.min(Number(limit) || 24, 50);
  const start = (p - 1) * l;
  return { items: rows.slice(start, start + l), page: p, limit: l, hasMore: start + l < rows.length, total: rows.length };
};
const cursorPage = (rows, { cursor, limit = 20 } = {}) => {
  const start = Number(cursor) || 0;
  const l = Math.min(Number(limit) || 20, 50);
  return { items: rows.slice(start, start + l), nextCursor: start + l < rows.length ? String(start + l) : null };
};

const likeKey = (type, id) => `${type}:${id}`;
const likeMap = () => readStore("likes2", {});
const likedChar = (id) => readStore("likes", []).includes(id);
const baseLikes = (type, id) => {
  if (type === "character") return (SAMPLE_CHARACTERS.find((c) => c.id === id) || mineRows().find((c) => c.id === id))?.likeCount || 0;
  if (type === "universe") return SAMPLE_UNIVERSES.find((u) => u.id === id)?.likeCount || 0;
  if (type === "post") return SAMPLE_POSTS.find((p) => p.id === id)?.likeCount || 0;
  return 0;
};

const sorters = {
  trending: (a, b) => b.likeCount - a.likeCount,
  new: (a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")),
  popular: (a, b) => b.messageCount - a.messageCount,
};

const MOCK_USERS_VIEW = (u) => ({
  id: u.id, email: u.email, displayName: u.displayName, username: u.username, photoURL: u.photoURL || null, bio: u.description || "",
  coins: u.tokensLeft, userType: u.user_type, banned: !!u.banned, banReason: u.banReason || "", createdAt: u.createdAt, termsAcceptedAt: u.termsAcceptedAt,
  followerCount: 0, followingCount: readStore("follows", []).length, characterCount: mineRows().filter((c) => c.creatorId === u.id).length,
  messageCount: 0, socialLinks: u.socialLinks || [], backgroundImage: u.backgroundImage || null,
});

// ---- config ตารางแอดมิน -------------------------------------------------------
const cfgSeed = {
  character_tags: () => MOCK_TAGS.map((t) => ({ ...t })),
  packages: () =>
    SAMPLE_PACKAGES.map((p, i) => ({
      id: `pk_${p.id}`, name: p.name, icon: p.emoji, tagline: "", price: p.price, baseCoins: Math.round(p.tokens / (1 + p.bonusPercent / 100)),
      bonusCoins: p.tokens - Math.round(p.tokens / (1 + p.bonusPercent / 100)), bonusPercent: p.bonusPercent, vipPoints: p.clubPoint, maxMessages: Math.round(p.tokens / 10), tag: p.tag, sort: i + 1, isActive: true,
    })),
  vip_tiers: () => CLUB_TIERS.map((n, i) => ({ id: n.toLowerCase(), name: n, minPoints: [0, 190, 600, 1500, 3000, 6000][i], monthlyCoins: i * 100, checkinMultiplier: 1 + i * 0.1, noteLimit: 500 * (i + 1), perks: [], sort: i + 1, isActive: true })),
  ai_models: () => AI_MODELS.map((m, i) => ({ ...m, maxTokens: 1024, temperature: 0.9, llmModel: "", styleHint: "", isNew: false, isDefault: !!m.isDefault, isActive: true, sort: i + 1 })),
  gifts: () => SAMPLE_GIFTS.map((g, i) => ({ id: g.id, name: g.name, nameTh: g.name, icon: g.emoji, coinCost: g.coinCost, rewardType: "", rewardDuration: 0, category: "general", isActive: true, sort: i + 1 })),
  promo_codes: () => [
    { id: "WELCOME50", coins: 50, maxUses: 1000, usedCount: 12, perUser: 1, expiresAt: null, isActive: true, note: "ต้อนรับสมาชิกใหม่" },
    { id: "AITALK100", coins: 100, maxUses: 200, usedCount: 3, perUser: 1, expiresAt: null, isActive: true, note: "แคมเปญเปิดตัว" },
  ],
};
const cfgRows = (table) => {
  if (!cfgSeed[table]) fail("ไม่รู้จักตาราง", "bad_request");
  const saved = readStore(`cfg:${table}`, null);
  if (saved) return saved;
  const seeded = cfgSeed[table]().map((r) => ({ ...r, createdAt: nowIso(), updatedAt: nowIso() }));
  writeStore(`cfg:${table}`, seeded);
  return seeded;
};

const DEFAULT_SETTINGS = {
  signup_coins: 50, checkin_coins: 30, checkin_bonus_every: 10, checkin_bonus_coins: 30, checkin_bonus_coins_pro: 60, referral_inviter_coins: 50,
  referral_invitee_coins: 50, referral_max_invites: 5, gift_creator_share: 0.3, char_approval_required: false, max_chars_per_user: 50, context_messages: 16,
  max_message_len: 6000, topup_require_init: true, ai_daily_free_limit: 20, announcement: "",
};

const NOTIFS = () => [
  { id: "nt1", category: "system", title: "ยินดีต้อนรับสู่ AITalk", body: "รับคอยน์ฟรีทุกวันที่หน้า \"รางวัล\"", link: "/rewards", createdAt: nowIso() },
  { id: "nt2", category: "character", title: "ตัวละครใหม่จากครีเอเตอร์ที่คุณติดตาม", body: "ลูน่า นักดูดาว เปิดตัวแล้ว", link: "/c/c4", createdAt: daysAgo(0.04) },
  { id: "nt3", category: "comment", title: "มีคนตอบความคิดเห็นของคุณ", body: "\"ชอบตัวละครนี้มาก!\"", link: "/c/c1", createdAt: daysAgo(1) },
  { id: "nt4", category: "follower", title: "มีผู้ติดตามใหม่", body: "มินต์ดาว เริ่มติดตามคุณ", link: "/creator/u1", createdAt: daysAgo(2) },
];

const draftFromIdea = (idea) => {
  const t = String(idea || "").trim();
  const name = t.split(/[\s,.!?]+/).filter(Boolean)[0]?.slice(0, 12) || "เพื่อนใหม่";
  return {
    name,
    tagline: t.slice(0, 90) || "เพื่อนใหม่ที่พร้อมคุยกับคุณ",
    description: `{{char}} คือตัวละครที่ถูกสร้างจากแนวคิดนี้: ${t}\nบุคลิก: อบอุ่น เป็นกันเอง พูดจาสุภาพ สนใจเรื่องของ {{user}}\nแนวทาง: ตอบสั้นกระชับ ใช้ *การกระทำ* คั่นบทพูด และไม่พูดถึงเนื้อหาไม่เหมาะสม`,
    greeting: `*ยิ้มทักทาย* "สวัสดี {{user}} ยินดีที่ได้รู้จักนะ วันนี้เป็นยังไงบ้าง"`,
    tagIds: ["ธงขาว", "ผจญภัย"].filter((x) => MOCK_TAGS.some((m) => m.id === x)),
    flag: "white",
  };
};

// ---- handlers ------------------------------------------------------------------
export const contentHandlers = {
  "tags.list": () => ({ items: MOCK_TAGS }),

  "characters.list": ({ page, limit, sort = "trending", tags, q, flag, creator }) => {
    let rows = visibleChars();
    const tagList = String(tags || "").split(",").filter(Boolean);
    if (tagList.length) rows = rows.filter((c) => tagList.every((t) => c.tagIds.includes(t)));
    if (q) rows = rows.filter((c) => (c.name + c.tagline + c.tagIds.join(" ")).toLowerCase().includes(String(q).toLowerCase()));
    if (flag) rows = rows.filter((c) => c.flag === flag);
    if (creator) rows = rows.filter((c) => c.createdBy === creator);
    if (sort === "following") {
      const f = readStore("follows", []);
      rows = rows.filter((c) => f.includes(c.createdBy));
    } else rows = [...rows].sort(sorters[sort] || sorters.trending);
    return paginate(rows, { page, limit });
  },
  "characters.byCode": ({ code }) => {
    const k = String(code || "").trim().toUpperCase();
    if (!/^[A-Z2-9]{7}$/.test(k)) fail("โค้ดต้องเป็นตัวอักษร A-Z/2-9 จำนวน 7 ตัว", "bad_request");
    const c = visibleChars().find((x) => x.shareCode === k) || fail("ไม่พบตัวละครจากโค้ดนี้", "not_found");
    const { description, ...card } = c;
    return { character: card };
  },
  "characters.get": ({ id }) => {
    const c = publicChars().find((x) => x.id === id) || draftRows().map((d) => toGs(d, "draft")).find((x) => x.id === id) || fail("ไม่พบตัวละครนี้", "not_found");
    const owner = c.createdBy === me().id;
    const likes = baseLikes("character", id) + (likedChar(id) ? 1 : 0);
    const { description, ...rest } = c;
    return { ...rest, ...(owner ? { description } : {}), likeCount: likes, isLiked: likedChar(id), isSaved: !!likeMap()[likeKey("save", id)], isOwner: owner };
  },
  "characters.mine": () => {
    const u = me();
    const rows = [...draftRows().map((c) => toGs(c, "draft")), ...mineRows().map((c) => toGs(c))].filter((c) => c.createdBy === u.id);
    return { items: rows.sort((a, b) => String(b.updatedAt || b.createdAt).localeCompare(String(a.updatedAt || a.createdAt))) };
  },
  "characters.save": (d) => {
    const u = me();
    if (d.contentRating === "NC") fail("เว็บนี้ไม่รองรับเนื้อหา 18+ (NC)", "bad_request");
    if (!d.draft) {
      if (!String(d.name || "").trim()) fail("กรุณากรอกชื่อตัวละคร", "bad_request");
      if (!String(d.tagline || "").trim()) fail("กรุณากรอกคำโปรย", "bad_request");
      if (!String(d.description || "").trim()) fail("กรุณากรอกคำอธิบายตัวละคร", "bad_request");
      if (!String(d.greeting || "").trim()) fail("กรุณากรอกคำทักทายเริ่มต้น", "bad_request");
      if (!d.image) fail("กรุณาเพิ่มรูปโปรไฟล์อย่างน้อย 1 รูป", "bad_request");
    } else if (!String(d.name || "").trim()) fail("ตั้งชื่อตัวละครก่อนบันทึกร่าง", "bad_request");
    const old = [...mineRows(), ...draftRows()].find((c) => c.id === d.id);
    if (old && old.creatorId !== u.id) fail("ไม่ใช่เจ้าของตัวละคร", "forbidden");
    const { draft, ...rest } = d;
    // เผยแพร่ครั้งแรกใช้ 1 สิทธิ์ (super_user ไม่ใช้) — บันทึก creditUsedAt กันหักซ้ำ
    let creditUsedAt = old?.creditUsedAt;
    if (!draft && !creditUsedAt && u.user_type !== "super_user" && (!old || old.status === "draft" || draftRows().some((c) => c.id === old.id))) {
      const have = u.createCredits || 0;
      if (have < 1) fail(`สิทธิ์สร้างตัวละครไม่พอ ต้องการ 1 สิทธิ์ แต่มี ${have} สิทธิ์`, "insufficient_credits");
      mockUsers.save({ ...u, createCredits: have - 1 });
      creditUsedAt = nowIso();
    }
    const row = {
      emoji: "✨", colors: ["#8b5cf6", "#ec4899"], messageCount: 0, likeCount: 0, chatCount: 0, features: [], ...old, ...rest,
      ...(creditUsedAt ? { creditUsedAt } : {}), id: d.id || uid("c_"), tags: d.tagIds || [], tagIds: d.tagIds || [], creatorId: u.id, createdAt: old?.createdAt || nowIso(), updatedAt: nowIso(),
    };
    writeStore("myCharacters", mineRows().filter((c) => c.id !== row.id));
    writeStore("drafts", draftRows().filter((c) => c.id !== row.id));
    if (draft) writeStore("drafts", [{ ...row, status: "draft" }, ...draftRows()]);
    else writeStore("myCharacters", [{ ...row, status: "approved" }, ...mineRows()]);
    return { character: toGs(row, draft ? "draft" : "approved") };
  },
  "characters.archive": ({ id, archived }) => {
    const list = mineRows();
    const row = list.find((c) => c.id === id) || fail("ไม่พบตัวละคร", "not_found");
    writeStore("myCharacters", list.map((c) => (c.id === id ? { ...c, isArchived: !!archived } : c)));
    return { character: toGs({ ...row, isArchived: !!archived }) };
  },
  "ai.characterDraft": ({ idea }) => {
    if (!String(idea || "").trim()) fail("กรุณาอธิบายแนวคิดตัวละคร", "bad_request");
    return { draft: draftFromIdea(idea), mock: true };
  },
  "ai.search": ({ q }) => {
    const words = String(q || "").toLowerCase().split(/[\s,]+/).filter((w) => w.length > 1);
    const tagIds = MOCK_TAGS.filter((t) => words.some((w) => t.name.toLowerCase().includes(w) || w.includes(t.name.toLowerCase()))).map((t) => t.id);
    const rows = visibleChars().filter((c) => {
      const hay = (c.name + c.tagline + c.tagIds.join(" ")).toLowerCase();
      return words.some((w) => hay.includes(w)) || tagIds.some((t) => c.tagIds.includes(t));
    });
    return { items: rows.sort(sorters.popular).slice(0, 12), interpreted: { keywords: words, tagIds }, mock: true };
  },

  "like.toggle": ({ targetType, targetId }) => {
    if (targetType === "character") {
      const likes = readStore("likes", []);
      const next = likes.includes(targetId) ? likes.filter((x) => x !== targetId) : [...likes, targetId];
      writeStore("likes", next);
      return { liked: next.includes(targetId), likeCount: baseLikes("character", targetId) + (next.includes(targetId) ? 1 : 0) };
    }
    const m = likeMap();
    const k = likeKey(targetType, targetId);
    m[k] = !m[k];
    writeStore("likes2", m);
    return { liked: !!m[k], likeCount: baseLikes(targetType, targetId) + (m[k] ? 1 : 0) };
  },
  "likes.mine": ({ targetType }) => {
    const ids = targetType === "character" ? readStore("likes", []) : Object.keys(likeMap()).filter((k) => k.startsWith(`${targetType}:`) && likeMap()[k]).map((k) => k.slice(targetType.length + 1));
    return { items: ids.map((targetId) => ({ targetId, createdAt: nowIso() })), nextCursor: null };
  },
  "follow.toggle": ({ userId }) => {
    const f = readStore("follows", []);
    const next = f.includes(userId) ? f.filter((x) => x !== userId) : [...f, userId];
    writeStore("follows", next);
    return { following: next.includes(userId), followerCount: (creatorOf(userId)?.followerCount || 0) + (next.includes(userId) ? 1 : 0) };
  },
  "profile.get": ({ userId, username }) => {
    const sc = SAMPLE_CREATORS.find((c) => c.id === userId || c.username === username);
    const real = Object.values(mockUsers.all()).find((u) => u.id === userId || u.username === username);
    const base = sc || (real && MOCK_USERS_VIEW(real)) || fail("ไม่พบผู้ใช้นี้", "not_found");
    const isMe = base.id === me().id;
    const chars = visibleChars().filter((c) => c.createdBy === base.id && (c.visibility === "public" || isMe));
    const f = readStore("follows", []).includes(base.id);
    return {
      user: { ...base, photoURL: base.photoURL || null, followerCount: (base.followerCount || 0) + (f ? 1 : 0), characterCount: chars.length, messageCount: chars.reduce((s, c) => s + (c.messageCount || 0), 0) || base.messageCount || 0, socialLinks: base.socialLinks || [] },
      isFollowing: f,
      characters: chars,
    };
  },
  "creators.list": ({ sort = "followers", q, page, limit }) => {
    const users = Object.values(mockUsers.all()).map(MOCK_USERS_VIEW).filter((u) => u.characterCount > 0);
    const chars = visibleChars();
    let rows = [...SAMPLE_CREATORS, ...users].map((c) => {
      const mine = chars.filter((x) => x.createdBy === c.id);
      return {
        id: c.id, displayName: c.displayName, username: c.username, photoURL: c.photoURL || null, colors: c.colors,
        followerCount: c.followerCount || 0, characters: mine.length, chats: mine.reduce((s, x) => s + (x.chatCount || 0), 0),
        messages: mine.reduce((s, x) => s + (x.messageCount || 0), 0), likes: mine.reduce((s, x) => s + (x.likeCount || 0), 0), gifts: mine.reduce((s, x) => s + (x.giftCount || 0), 0),
      };
    });
    if (q) rows = rows.filter((c) => (c.displayName + c.username).toLowerCase().includes(String(q).toLowerCase()));
    const key = { followers: "followerCount", messages: "messages", chats: "chats", characters: "characters", likes: "likes" }[sort] || "followerCount";
    rows = rows.sort((a, b) => b[key] - a[key]).map((c, i) => ({ ...c, rank: i + 1 }));
    const mine = rows.find((c) => c.id === me().id) || null;
    return { ...paginate(rows, { page, limit }), me: mine };
  },
  "rankings.overview": ({ period = "week" }) => {
    const chars = visibleChars();
    const mul = period === "day" ? 0.15 : 1;
    const top = (arr, f, vf) => [...arr].sort((a, b) => vf(b) - vf(a)).slice(0, 10).map((x, i) => f(x, i));
    const cardRow = (key) => (c, i) => ({ rank: i + 1, id: c.id, name: c.name, image: c.image, emoji: c.emoji, colors: c.colors, creatorName: c.creatorName, value: Math.round((c[key] || 0) * mul) });
    const creators = SAMPLE_CREATORS.map((c) => ({ ...c, chats: chars.filter((x) => x.createdBy === c.id).reduce((s, x) => s + x.chatCount, 0), likes: chars.filter((x) => x.createdBy === c.id).reduce((s, x) => s + x.likeCount, 0), gifts: chars.filter((x) => x.createdBy === c.id).reduce((s, x) => s + x.giftCount, 0) }));
    const crow = (key) => (c, i) => ({ rank: i + 1, id: c.id, displayName: c.displayName, username: c.username, photoURL: null, colors: c.colors, value: Math.round(c[key] * mul) });
    return {
      period, since: daysAgo(period === "day" ? 1 : 7),
      characters: { chats: top(chars, cardRow("chatCount"), (c) => c.chatCount), likes: top(chars, cardRow("likeCount"), (c) => c.likeCount), gifts: top(chars, cardRow("giftCount"), (c) => c.giftCount) },
      creators: { chats: top(creators, crow("chats"), (c) => c.chats), likes: top(creators, crow("likes"), (c) => c.likes), gifts: top(creators, crow("gifts"), (c) => c.gifts) },
    };
  },
  "gifts.ranking": ({ characterId, period = "month" }) => {
    const names = ["มินต์ดาว", "ครูตูน", "บ้านป้อม", "ราตรีเอ็กซ์", "นกฮูกกลางคืน"];
    return { characterId, period, items: names.map((n, i) => ({ rank: i + 1, userId: `u${i}`, displayName: n, photoURL: null, coins: (5 - i) * 120 + (String(characterId).length % 7) * 10, count: 5 - i })) };
  },

  "universes.list": ({ page, limit, q, mine }) => {
    const own = readStore("myUniverses", []);
    let rows = [...own, ...SAMPLE_UNIVERSES].map((u) => ({ ...u, tagIds: u.tagIds || [], createdBy: u.creatorId, creatorName: creatorName(u.creatorId), characterCount: (u.characterIds || []).length, createdAt: u.createdAt || daysAgo(10) }));
    if (mine) rows = rows.filter((u) => u.createdBy === me().id);
    if (q) rows = rows.filter((u) => (u.name + u.tagline).toLowerCase().includes(String(q).toLowerCase()));
    return paginate(rows.sort((a, b) => (b.messageCount || 0) - (a.messageCount || 0)), { page, limit });
  },
  "universes.get": ({ id }) => {
    const u = [...readStore("myUniverses", []), ...SAMPLE_UNIVERSES].find((x) => x.id === id) || fail("ไม่พบจักรวาลนี้", "not_found");
    const chars = (u.characterIds || []).map((cid) => visibleChars().find((c) => c.id === cid)).filter(Boolean);
    const likes = baseLikes("universe", id) + (likeMap()[likeKey("universe", id)] ? 1 : 0);
    return { universe: { ...u, tagIds: u.tagIds || [], createdBy: u.creatorId, creatorName: creatorName(u.creatorId), likeCount: likes, characterCount: chars.length, isLiked: !!likeMap()[likeKey("universe", id)], isOwner: u.creatorId === me().id, createdAt: u.createdAt || daysAgo(10) }, characters: chars };
  },
  "universes.save": (d) => {
    const u = me();
    if (!String(d.name || "").trim()) fail("กรุณากรอกชื่อจักรวาล", "bad_request");
    if (!(d.characterIds || []).length) fail("เลือกตัวละครอย่างน้อย 1 ตัว", "bad_request");
    const list = readStore("myUniverses", []);
    const old = list.find((x) => x.id === d.id);
    const row = { emoji: "🌍", colors: ["#6366f1", "#ec4899"], messageCount: 0, likeCount: 0, ...old, ...d, id: d.id || uid("un_"), creatorId: u.id, createdAt: old?.createdAt || nowIso() };
    writeStore("myUniverses", [row, ...list.filter((x) => x.id !== row.id)]);
    return { universe: row };
  },

  "personas.list": () => ({ items: readStore("personas", []) }),
  "personas.save": (d) => {
    if (!String(d.name || "").trim()) fail("กรุณากรอกชื่อบทบาท", "bad_request");
    const row = { id: d.id || uid("pe_"), userId: me().id, ...d };
    writeStore("personas", [row, ...readStore("personas", []).filter((x) => x.id !== row.id)]);
    return { persona: row };
  },
  "scenarios.list": () => ({ items: readStore("scenarios", []) }),
  "scenarios.save": (d) => {
    if (!String(d.name || "").trim() || !String(d.detail || "").trim()) fail("กรุณากรอกชื่อและรายละเอียดสถานการณ์", "bad_request");
    const row = { id: d.id || uid("sc_"), userId: me().id, ...d };
    writeStore("scenarios", [row, ...readStore("scenarios", []).filter((x) => x.id !== row.id)]);
    return { scenario: row };
  },

  "comments.list": ({ targetType, targetId, sort = "new" }) => {
    const rows = readStore("comments", []).filter((c) => c.targetType === targetType && c.targetId === targetId && !c.isDeleted);
    rows.sort((a, b) => (sort === "top" ? b.likeCount - a.likeCount : 0) || String(b.createdAt).localeCompare(String(a.createdAt)));
    return { items: rows, nextCursor: null };
  },
  "comment.add": ({ targetType, targetId, text, parentId }) => {
    const t = String(text || "").trim();
    if (!t) fail("กรุณาพิมพ์ความคิดเห็น", "bad_request");
    if (t.length > 1000) fail("ความคิดเห็นยาวเกิน 1,000 ตัวอักษร", "bad_request");
    const u = me();
    const row = { id: uid("cm_"), targetType, targetId, userId: u.id, userName: u.displayName, userPhoto: u.photoURL || null, text: t, parentId: parentId || null, isPin: false, isDeleted: false, likeCount: 0, creatorLiked: false, createdAt: nowIso() };
    writeStore("comments", [row, ...readStore("comments", [])]);
    return { comment: row };
  },
  "comment.delete": ({ id }) => {
    writeStore("comments", readStore("comments", []).map((c) => (c.id === id ? { ...c, isDeleted: true } : c)));
    return { ok: true };
  },

  "posts.list": ({ type = "post", userId, characterId, cursor, limit }) => {
    const seeded = SAMPLE_POSTS.map((p, i) => ({ type: "post", userName: creatorName(p.userId), userPhoto: null, media: [], visibility: "public", createdAt: daysAgo(i + 0.2), ...p }));
    const shorts = SAMPLE_CHARACTERS.slice(0, 4).map((c, i) => ({ id: `s_${c.id}`, type: "short", userId: c.creatorId, userName: creatorName(c.creatorId), userPhoto: null, text: c.tagline, media: [], characterIds: [c.id], visibility: "public", likeCount: c.likeCount, commentCount: 0, createdAt: daysAgo(i) }));
    const u = me();
    let rows = [...readStore("posts", []), ...seeded, ...shorts].filter((p) => p.type === type && !p.isDeleted && (p.visibility === "public" || p.userId === u.id));
    if (userId) rows = rows.filter((p) => p.userId === userId);
    if (characterId) rows = rows.filter((p) => (p.characterIds || []).includes(characterId));
    return cursorPage(rows, { cursor, limit });
  },
  "post.add": ({ type = "post", text, media = [], characterIds = [], visibility = "public" }) => {
    const t = String(text || "").trim();
    if (!t && !media.length) fail("กรุณาพิมพ์ข้อความหรือแนบสื่อ", "bad_request");
    if (type === "short" && !media.length) fail("คลิปสั้นต้องมีลิงก์วิดีโอ", "bad_request");
    const u = me();
    const row = { id: uid("po_"), userId: u.id, userName: u.displayName, userPhoto: u.photoURL || null, type, text: t, media, characterIds, visibility, likeCount: 0, commentCount: 0, isDeleted: false, createdAt: nowIso() };
    writeStore("posts", [row, ...readStore("posts", [])]);
    return { post: row };
  },
  "post.delete": ({ id }) => {
    writeStore("posts", readStore("posts", []).map((p) => (p.id === id ? { ...p, isDeleted: true } : p)));
    return { ok: true };
  },

  "report.create": ({ targetType, targetId, category, details, contact }) => {
    if (!category) fail("กรุณาเลือกประเภทการรายงาน", "bad_request");
    const row = { id: uid("rp_"), reporterId: me().id, targetType, targetId, category, details: details || "", contact: contact || "", status: "open", resolution: "", createdAt: nowIso() };
    writeStore("reports", [row, ...readStore("reports", [])]);
    return { id: row.id };
  },

  "notifications.list": ({ category }) => {
    const read = readStore("notifRead", []);
    const items = NOTIFS().map((n) => ({ ...n, isRead: read.includes(n.id) })).filter((n) => !category || category === "all" || n.category === category);
    return { items, nextCursor: null, unread: items.filter((n) => !n.isRead).length };
  },
  "notifications.read": ({ id, all }) => {
    const ids = all ? NOTIFS().map((n) => n.id) : [id];
    writeStore("notifRead", Array.from(new Set([...readStore("notifRead", []), ...ids])));
    return { unread: NOTIFS().filter((n) => !readStore("notifRead", []).includes(n.id)).length };
  },

  "upload": ({ mime, data }) => {
    if (!data) fail("ไม่พบข้อมูลไฟล์", "bad_request");
    return { url: `data:image/${mime};base64,${data}`, id: uid("up_") };
  },
  "me.update": (d) => {
    const user = me();
    const patch = {};
    if (d.displayName !== undefined) patch.displayName = String(d.displayName).slice(0, 20);
    if (d.username !== undefined) {
      const clash = Object.values(mockUsers.all()).find((u) => u.username === d.username && u.id !== user.id);
      if (clash) fail("ชื่อผู้ใช้นี้ถูกใช้แล้ว", "bad_request");
      patch.username = String(d.username).slice(0, 20);
    }
    if (d.bio !== undefined) patch.description = String(d.bio).slice(0, 500);
    ["photoURL", "backgroundImage", "socialLinks"].forEach((k) => d[k] !== undefined && (patch[k] = d[k]));
    if (d.preferences) patch.preferences = d.preferences;
    return { user: mockUsers.save({ ...user, ...patch }) };
  },
  "me.get": () => {
    const { password, ...u } = me(); // eslint-disable-line no-unused-vars
    return { user: { ...u, bio: u.description, coins: u.tokensLeft, userType: u.user_type } };
  },

  // ---- admin -------------------------------------------------------------------
  "admin.stats": ({ days = 14 }) => {
    requireSuper();
    const n = Math.min(Number(days) || 14, 60);
    const list = Array.from({ length: n }, (_, i) => {
      const d = new Date(Date.now() - (n - 1 - i) * 864e5);
      const s = (d.getDate() * 37 + i * 13) % 50;
      return { date: d.toISOString().slice(0, 10), newUsers: 5 + (s % 17), topupAmount: 400 + s * 55, topupCount: 3 + (s % 9), coinsSpent: 1500 + s * 90, messages: 300 + s * 25 };
    });
    const sum = (k) => list.reduce((a, b) => a + b[k], 0);
    return { days: list, totals: { users: Object.keys(mockUsers.all()).length + 120, topupAmount: sum("topupAmount"), topupCount: sum("topupCount"), coinsSpent: sum("coinsSpent"), messages: sum("messages"), characters: publicChars().length } };
  },
  "admin.users.list": ({ q, cursor, limit }) => {
    requireSuper();
    let rows = Object.values(mockUsers.all()).map(MOCK_USERS_VIEW);
    if (q) rows = rows.filter((u) => (u.email + u.displayName + u.username).toLowerCase().includes(String(q).toLowerCase()));
    return cursorPage(rows, { cursor, limit });
  },
  "admin.users.get": ({ id }) => {
    requireSuper();
    const u = Object.values(mockUsers.all()).find((x) => x.id === id) || fail("ไม่พบผู้ใช้", "not_found");
    return { user: MOCK_USERS_VIEW(u), vip: { tier: u.tier }, coinTx: readStore("coinTx", []).filter((t) => t.userId === id).slice(0, 30).map((t) => ({ ...t, amount: t.coinUsed })) };
  },
  "admin.users.ban": ({ id, banned, reason }) => {
    requireSuper();
    const u = Object.values(mockUsers.all()).find((x) => x.id === id) || fail("ไม่พบผู้ใช้", "not_found");
    return { user: MOCK_USERS_VIEW(mockUsers.save({ ...u, banned: !!banned, banReason: banned ? reason || "" : "" })) };
  },
  "admin.users.adjustCoins": ({ id, amount, reason }) => {
    requireSuper();
    const amt = Number(amount);
    if (!amt || !Number.isFinite(amt)) fail("ระบุจำนวนคอยน์ (+/-)", "bad_request");
    if (!String(reason || "").trim()) fail("กรุณาระบุเหตุผล", "bad_request");
    const u = Object.values(mockUsers.all()).find((x) => x.id === id) || fail("ไม่พบผู้ใช้", "not_found");
    const next = Math.max(0, (u.tokensLeft || 0) + amt);
    mockUsers.save({ ...u, tokensLeft: next });
    const tx = { id: uid("tx_"), userId: id, createdAt: nowIso(), usageType: "admin_adjust", coinUsed: amt, amount: amt, coinsBeforeUse: u.tokensLeft, balanceAfter: next, note: reason };
    writeStore("coinTx", [tx, ...readStore("coinTx", [])].slice(0, 300));
    return { balance: next, tx };
  },
  "admin.characters.list": ({ status, q, cursor, limit }) => {
    requireSuper();
    let rows = [...draftRows().map((c) => toGs(c, "draft")), ...publicChars()];
    if (status) rows = rows.filter((c) => c.status === status || (status === "featured" && c.isFeatured));
    if (q) rows = rows.filter((c) => (c.name + c.creatorName).toLowerCase().includes(String(q).toLowerCase()));
    return cursorPage(rows, { cursor, limit });
  },
  "admin.characters.review": ({ id, decision }) => {
    requireSuper();
    const rv = reviews();
    const cur = rv[id] || {};
    const map = { approve: { status: "approved" }, hide: { status: "hidden" }, reject: { status: "rejected" }, feature: { isFeatured: true }, unfeature: { isFeatured: false } };
    if (!map[decision]) fail("ไม่รู้จักการตัดสินใจ", "bad_request");
    rv[id] = { ...cur, ...map[decision] };
    writeStore("reviews", rv);
    return { character: toGs(publicChars().find((c) => c.id === id) || { id, name: id, tags: [] }) };
  },
  "admin.config.list": ({ table }) => {
    requireSuper();
    return { items: cfgRows(table) };
  },
  "admin.config.save": ({ table, data }) => {
    requireSuper();
    const rows = cfgRows(table);
    const id = data.id || uid(table === "packages" ? "pk_" : "x_");
    const old = rows.find((r) => r.id === id);
    const item = { isActive: true, ...old, ...data, id, createdAt: old?.createdAt || nowIso(), updatedAt: nowIso() };
    writeStore(`cfg:${table}`, old ? rows.map((r) => (r.id === id ? item : r)) : [...rows, item]);
    return { item };
  },
  "admin.config.remove": ({ table, id }) => {
    requireSuper();
    const rows = cfgRows(table);
    const item = rows.find((r) => r.id === id) || fail("ไม่พบรายการ", "not_found");
    const next = { ...item, isActive: false };
    writeStore(`cfg:${table}`, rows.map((r) => (r.id === id ? next : r)));
    return { item: next };
  },
  "admin.reports.list": ({ status, cursor, limit }) => {
    requireSuper();
    let rows = readStore("reports", []);
    if (status) rows = rows.filter((r) => r.status === status);
    return cursorPage(rows, { cursor, limit });
  },
  "admin.reports.resolve": ({ id, resolution }) => {
    requireSuper();
    const rows = readStore("reports", []).map((r) => (r.id === id ? { ...r, status: "resolved", resolution: resolution || "", resolvedBy: me().id } : r));
    writeStore("reports", rows);
    return { report: rows.find((r) => r.id === id) };
  },
  "admin.topups.list": ({ status, cursor, limit }) => {
    requireSuper();
    const users = Object.values(mockUsers.all());
    const real = readStore("coinTx", []).filter((t) => t.usageType === "purchase").map((t) => ({ id: t.id, userId: t.userId, packageId: t.packageId || "", amount: Math.round((t.coinUsed || 0) / 8), coins: t.coinUsed, status: "credited", creditedAt: t.createdAt, createdAt: t.createdAt }));
    const fake = users.slice(0, 2).map((u, i) => ({ id: `tp_demo${i}`, userId: u.id, packageId: "pk_p3", amount: 149, coins: 1500, status: i ? "pending" : "credited", creditedAt: i ? null : daysAgo(1), createdAt: daysAgo(1 + i) }));
    let rows = [...real, ...fake];
    if (status) rows = rows.filter((r) => r.status === status);
    return cursorPage(rows, { cursor, limit });
  },
  "admin.settings.get": () => {
    requireSuper();
    return { settings: { ...DEFAULT_SETTINGS, ...readStore("settings", {}) } };
  },
  "admin.settings.set": ({ key, value }) => {
    requireSuper();
    const s = { ...readStore("settings", {}), [key]: value };
    writeStore("settings", s);
    return { settings: { ...DEFAULT_SETTINGS, ...s } };
  },
  "admin.cache.clear": () => {
    requireSuper();
    return { ok: true };
  },
};

export const runContentMock = async (action, params = {}) => {
  await new Promise((r) => setTimeout(r, MOCK_LATENCY));
  const fn = contentHandlers[action];
  if (!fn) throw new Error(`mock ยังไม่รองรับ action: ${action}`);
  return fn(params);
};
